// src/pages/BibleStudyArchivePage.js
// Updated: 08/24/2025 08:50 PM

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore';

import { Container, Typography, CircularProgress, Box, Button, Card, CardContent, CardActions, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import BibleStudyModal from '../components/BibleStudyModal';

function BibleStudyArchivePage() {
  const { userProfile } = useAuth();
  const [bibleStudies, setBibleStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isBibleStudyModalOpen, setIsBibleStudyModalOpen] = useState(false);
  const [selectedBibleStudy, setSelectedBibleStudy] = useState(null);

  const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  // Fetch Bible studies in real-time, ordered by date
  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }

    const bibleStudiesColRef = collection(db, 'churches', userProfile.churchId, 'bibleStudies');
    const q = query(bibleStudiesColRef, orderBy('date', 'desc')); // Order by date descending

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setBibleStudies(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Bible studies: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleAddBibleStudy = () => {
    setSelectedBibleStudy(null);
    setIsBibleStudyModalOpen(true);
  };

  const handleEditBibleStudy = (bibleStudy) => {
    setSelectedBibleStudy(bibleStudy);
    setIsBibleStudyModalOpen(true);
  };

  const handleSaveBibleStudy = async (bibleStudyData) => {
    if (!userProfile?.churchId) return;
    const bibleStudiesColRef = collection(db, 'churches', userProfile.churchId, 'bibleStudies');
    
    const dataToSave = {
        ...bibleStudyData,
        churchId: userProfile.churchId,
    };

    try {
      if (selectedBibleStudy?.id) {
        const docRef = doc(bibleStudiesColRef, selectedBibleStudy.id);
        await updateDoc(docRef, dataToSave);
      } else {
        await addDoc(bibleStudiesColRef, dataToSave);
      }
      setIsBibleStudyModalOpen(false);
      setSelectedBibleStudy(null);
    } catch (error) {
      console.error("Error saving Bible study:", error);
      throw error;
    }
  };

  const handleDeleteBibleStudy = async (bibleStudyId) => {
    if (!userProfile?.churchId || !bibleStudyId) return;
    if (window.confirm('Are you sure you want to delete this Bible study entry?')) {
      try {
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'bibleStudies', bibleStudyId));
        alert('Bible study deleted.');
      } catch (error) {
        console.error("Error deleting Bible study:", error);
        alert('Failed to delete Bible study.');
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }
  if (!userProfile?.churchId) {
    return <Container><Typography>Please ensure your user profile has a church assigned to view the Bible study archive.</Typography></Container>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Bible Study Archive</Typography>
        {isLeadership && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBibleStudy}>
            Add Bible Study
          </Button>
        )}
      </Box>

      {bibleStudies.length === 0 && (
        <Typography>No Bible studies available in the archive at this time.</Typography>
      )}

      <Grid container spacing={3}>
        {bibleStudies.map((study) => (
          <Grid item key={study.id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h2">{study.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Teacher: {study.speaker}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Date: {study.date?.toDate().toLocaleDateString()}
                </Typography>
                {study.description && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {study.description}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                {study.videoLink && (
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    href={study.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                  >
                    Watch Bible Study
                  </Button>
                )}
                {isLeadership && (
                  <Box>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditBibleStudy(study)}>
                      Edit
                    </Button>
                    <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => handleDeleteBibleStudy(study.id)}>
                      Delete
                    </Button>
                  </Box>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <BibleStudyModal
        open={isBibleStudyModalOpen}
        onClose={() => setIsBibleStudyModalOpen(false)}
        onSave={handleSaveBibleStudy}
        bibleStudy={selectedBibleStudy}
      />
    </Container>
  );
}

export default BibleStudyArchivePage;